// @oasis-permission-public: open to any signed-in Oasis user
import { Icon } from "@mirage-web/icons";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import z from "zod";

import { Button } from "~/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { sessionStorageDef } from "~/modules/auth/credentials";
import { createSessionMutation } from "~/modules/auth/network";

export const Route = createFileRoute("/_dash/session")({
  component: RouteComponent,
  validateSearch: z.object({
    redirectTo: z.string().optional(),
  }),
});

const DURATION_OPTIONS = [
  { label: "1 day", value: 24 * 60 * 60 * 1000 },
  { label: "1 week", value: 7 * 24 * 60 * 60 * 1000 },
  { label: "1 month", value: 30 * 24 * 60 * 60 * 1000 },
  { label: "1 year", value: 365 * 24 * 60 * 60 * 1000 },
];

function RouteComponent() {
  const { redirectTo } = Route.useSearch();
  const [session, setSession] = sessionStorageDef.use();
  const [justification, setJustification] = useState("");
  const [duration, setDuration] = useState<string>(DURATION_OPTIONS.at(-1)?.value.toString() || "");
  const navigate = useNavigate();

  const { mutate: createSession, isPending } = createSessionMutation.useMutation({
    onSuccess(result, variables) {
      setSession({
        sessionId: result.sessionId,
        expiresAt: result.expiresAt,
        justification: variables.params.justification,
      });

      toast.success("Session created successfully");
      setJustification("");
      setDuration("");

      if (redirectTo) {
        navigate({ to: redirectTo });
      }
    },
    onError() {
      toast.error("Failed to create session");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!justification.trim()) {
      toast.error("Please provide a justification");
      return;
    }

    if (!duration) {
      toast.error("Please select a duration");
      return;
    }

    createSession({
      params: {
        justification: justification.trim(),
        durationMs: parseInt(duration),
      },
    });
  };

  const formatExpirationTime = (expiresAt: number) => {
    const date = new Date(expiresAt);
    return date.toLocaleString();
  };

  const isSessionExpired = (expiresAt: number) => {
    return expiresAt < Date.now();
  };

  return (
    <main className="space-y-6">
      <div>
        <h1 className="text-xl font-bold">Session Management</h1>
      </div>

      {redirectTo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <div className="bg-blue-100 rounded-full p-1">
            <Icon name="info" className="size-4 text-blue-600" />
          </div>
          <div>
            <p className="text-blue-900 font-medium">Session Required</p>
            <p className="text-blue-700 text-sm mt-1">
              To access <span className="font-medium font-mono">{redirectTo}</span>, you need to add
              a justification below.
            </p>
          </div>
        </div>
      )}

      {session && (
        <Card>
          <CardHeader>
            <CardTitle>Current Session</CardTitle>
            <CardDescription>Your active elevated access session details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-sm font-medium">Session ID</div>
              <p className="text-sm text-muted-foreground font-mono">{session.sessionId}</p>
            </div>
            <div>
              <div className="text-sm font-medium">Justification</div>
              <p className="text-sm text-muted-foreground">{session.justification}</p>
            </div>
            <div>
              <div className="text-sm font-medium">Expires At</div>
              <p
                className={`text-sm ${isSessionExpired(session.expiresAt) ? "text-destructive" : "text-muted-foreground"}`}
              >
                {formatExpirationTime(session.expiresAt)}
                {isSessionExpired(session.expiresAt) && " (Expired)"}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Create New Session</CardTitle>
          <CardDescription>
            Request elevated access with proper justification and duration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="justification" className="text-sm font-medium">
                Justification *
              </label>
              <Input
                id="justification"
                placeholder="Enter the reason for elevated access..."
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="duration" className="text-sm font-medium">
                Duration *
              </label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select session duration" />
                </SelectTrigger>
                <SelectContent>
                  {DURATION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button type="submit" disabled={isPending} className="w-full">
              {isPending ? "Creating Session..." : "Create Session"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </main>
  );
}
